import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import type { MaClosureCell, MaClosureStripView } from './MetalAccountingLayer.transform';

/** One cell's presentation, shared by both `MetalAccountingStrip` instances
 *  (`/metal-accounting` and `/plant-model?layer=ma`), so the same read can
 *  never render two different ways across routes. `subTone` colours `sub`
 *  independently of `value`'s tone — the tails-loss cell needs it because
 *  `value` is an untoned loss fraction and `sub` is a tails grade toned
 *  against its own FMEA threshold (hard rule 1).
 *
 *  ma-stream round 2 (ruling A): `MineToShippedCard` no longer renders a
 *  tails-loss row of its own (`MF-1`/`MF-2`/`SF-6`) — the strip's cells are
 *  now the loss cells' only rendering on `/metal-accounting`, same as they
 *  already were on `/plant-model?layer=ma`. */
/** paul-fmea.A12 / #277: node id the Zn/Pb tails-loss cell opens a drill on —
 *  the same circuit ids `MetalAccountingLayer.transform.ts`'s
 *  `LOSS_DRILL_CIRCUIT` uses. Ag has no entry (matches `drillElement` never
 *  being set for Ag). */
const DRILL_NODE_ID: Record<'Zn' | 'Pb', string> = { Zn: 'sp-zn', Pb: 'sp-pb' };

export function MetalAccountingCell({
  cell,
  onSelectNode,
}: {
  cell: MaClosureCell;
  /** paul-fmea.A12 / #277: when set and `cell.drillElement` is present, the
   *  cell renders as a click affordance that selects that element's circuit
   *  node — "the losing circuit with the layer on" (loss-drill.md § UI
   *  states "Entry"). `undefined` elsewhere leaves the cell exactly as
   *  before this field existed. */
  onSelectNode?: (nodeId: string) => void;
}) {
  const drillNodeId = cell.drillElement && onSelectNode ? DRILL_NODE_ID[cell.drillElement] : undefined;
  const content = (
    <>
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted">{cell.label}</span>
      <span
        className="text-sm font-semibold tabular tracking-tight text-zinc-900 dark:text-zinc-100"
        style={cell.tone === 'neutral' ? undefined : { color: vizToneColor(cell.tone) }}
      >
        {cell.value}
      </span>
      {cell.sub ? (
        <span
          className="text-[10px] text-muted"
          style={cell.subTone ? { color: vizToneColor(cell.subTone) } : undefined}
        >
          {cell.sub}
        </span>
      ) : null}
    </>
  );

  if (drillNodeId) {
    return (
      <button
        type="button"
        onClick={() => onSelectNode?.(drillNodeId)}
        className="flex items-baseline gap-1.5 hover:underline"
        title={cell.title}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex items-baseline gap-1.5" title={cell.title}>
      {content}
    </div>
  );
}

/**
 * Compact closure strip under the process graph while the metal-accounting
 * layer is on (#163 judgement): the Module 2 headline read — closure KPIs,
 * balance anchor, in-circuit status — deep-linking to /metal-accounting for
 * the full registry surfaces. The layer gives the intertwining a spatial home
 * without demoting the module.
 *
 * `showChainGapCell` is false on /metal-accounting, where `MineToShippedCard`
 * carries the mine ↔ shipped read under this strip, and true on
 * /plant-model?layer=ma, the only route where the strip is that fact's sole carrier.
 * That cell renders on the caption row beside the deep link it leads to, so the
 * closure row stays one line at 1470px on both routes.
 */
export function MetalAccountingStrip({
  view,
  metalAccountingHref,
  showChainGapCell = true,
  onSelectNode,
}: {
  view: MaClosureStripView;
  metalAccountingHref?: string;
  showChainGapCell?: boolean;
  /** paul-fmea.A12 / #277: forwarded to each `MetalAccountingCell` — only the
   *  Zn/Pb tails-loss cells use it (`cell.drillElement`). */
  onSelectNode?: (nodeId: string) => void;
}) {
  const closureCells = view.cells.filter((cell) => cell.id !== 'ma-chain-gap');
  const chainCell = showChainGapCell ? view.cells.find((cell) => cell.id === 'ma-chain-gap') : undefined;
  const renderCell = (cell: MaClosureStripView['cells'][number]) => (
    <MetalAccountingCell key={cell.id} cell={cell} onSelectNode={onSelectNode} />
  );
  return (
    <Commentable name="MetalAccountingLayer">
      <GlassCard className="px-4 py-2.5">
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1.5">
          {closureCells.map(renderCell)}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-1.5">
          <p className="text-[10px] text-muted">{view.caption}</p>
          <ProvenanceChip text={view.provenanceChip} title={view.provenance} full />
          {chainCell || metalAccountingHref ? (
            <div className="ml-auto flex items-baseline gap-x-4">
              {chainCell ? renderCell(chainCell) : null}
              {metalAccountingHref ? (
                <Link
                  to={metalAccountingHref}
                  className="text-xs font-medium text-accent-700 dark:text-accent-400 hover:underline"
                >
                  Open Metal accounting →
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </GlassCard>
    </Commentable>
  );
}
